import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

const STORAGE_KEY = "carspa_popup_seen_v1";
const SCROLL_TRIGGER_PCT = 0.25; // open after user scrolls past 25% of page

export const Popup = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    let triggered = false;
    const onScroll = () => {
      if (triggered) return;
      const doc = document.documentElement;
      const pct =
        (doc.scrollTop || document.body.scrollTop) /
        (doc.scrollHeight - doc.clientHeight || 1);
      if (pct > SCROLL_TRIGGER_PCT) {
        triggered = true;
        setOpen(true);
        try {
          sessionStorage.setItem(STORAGE_KEY, "1");
        } catch (e) {}
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // Fallback: open after 12s anyway
    const t = setTimeout(() => {
      if (!triggered) {
        triggered = true;
        setOpen(true);
        try {
          sessionStorage.setItem(STORAGE_KEY, "1");
        } catch (e) {}
      }
    }, 12000);

    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(t);
    };
  }, []);

  // Close on escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  const goToForm = () => {
    setOpen(false);
    setTimeout(() => {
      document.getElementById("rezervace")?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  };

  return (
    <div
      data-testid="hormozi-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="popup-headline"
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-5 animate-fade-in"
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />

      {/* card */}
      <div
        data-testid="popup-card"
        className="relative w-full sm:max-w-xl bg-black border-2 border-hormozi-yellow shadow-[0_30px_60px_-15px_rgba(0,0,0,0.8)] overflow-hidden"
      >
        {/* yellow caution tape */}
        <div className="h-1.5 bg-hormozi-yellow" />

        {/* close */}
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="popup-close"
          aria-label="Zavřít"
          className="absolute top-3.5 right-3.5 z-10 w-9 h-9 flex items-center justify-center bg-black/60 hover:bg-hormozi-yellow hover:text-black text-white border border-white/20 hover:border-hormozi-yellow transition-colors"
        >
          <X size={18} strokeWidth={2.4} />
        </button>

        <div className="p-6 sm:p-8 md:p-10">
          {/* alert tag */}
          <div className="inline-flex items-center gap-2 bg-hormozi-yellow text-black px-3 py-1.5 mb-5">
            <span className="text-xs sm:text-sm tracking-wide uppercase font-black">
              ▲ Před tím, než zavřete
            </span>
          </div>

          <h2
            id="popup-headline"
            className="font-display text-3xl sm:text-4xl md:text-5xl text-white leading-[0.95] tracking-tight"
          >
            Detailing,<br />
            který platíte 2×.<br />
            <span className="text-hormozi-yellow">Aniž to víte.</span>
          </h2>

          <div className="mt-5 sm:mt-6 space-y-3 text-zinc-200 text-base sm:text-lg leading-snug font-medium">
            <p>
              <span className="text-white font-bold">10 let</span> připravujeme vozy pro velké autosalony a leasingové firmy.
              Oni naši práci <span className="text-white font-bold">přeprodávají</span> klientům jako svou — s vlastní marží navrch.
            </p>
            <p className="border-l-2 border-hormozi-yellow pl-3 sm:pl-4">
              Tentokrát žádný prostředník.
              <span className="text-hormozi-yellow font-black"> Stejné ruce. Stejné materiály. Polovina ceny.</span>
            </p>
          </div>

          {/* mini value compare */}
          <div className="mt-6 border-2 border-hormozi-yellow/60 bg-black/50">
            <div className="grid grid-cols-2 divide-x divide-white/10">
              <div className="p-4 sm:p-5">
                <div className="text-[10px] tracking-wide uppercase text-zinc-500 font-bold mb-1">
                  Přes prostředníka
                </div>
                <div className="font-display text-xl sm:text-2xl text-white line-through decoration-red-500/80 decoration-2 leading-none tabular-nums">
                  13 999 Kč
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">+ jejich marže</div>
              </div>
              <div className="p-4 sm:p-5 bg-hormozi-yellow text-black">
                <div className="text-[10px] tracking-wide uppercase font-black mb-1">
                  Přímo od nás
                </div>
                <div className="font-display text-2xl sm:text-3xl leading-none tabular-nums">
                  6 999 Kč
                </div>
                <div className="text-[10px] font-black mt-1">Ušetříte 7 000 Kč</div>
              </div>
            </div>
          </div>

          <button
            onClick={goToForm}
            data-testid="popup-cta"
            className="mt-6 w-full bg-hormozi-yellow hover:bg-[#FFE34D] text-black px-6 py-4 sm:py-5 text-sm sm:text-base tracking-tight uppercase font-black transition-colors"
          >
            Chci voucher přímo od CarSpa →
          </button>

          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid="popup-dismiss"
            className="mt-2 w-full text-[11px] sm:text-xs tracking-wide uppercase text-zinc-500 hover:text-zinc-300 py-2 font-bold"
          >
            Ne, raději zaplatím prostředníkovi
          </button>
        </div>
      </div>
    </div>
  );
};

export default Popup;
